import { Badge } from '@/components/ui/badge'
import { ICON_SIZE_2 } from '@/utils/constant'
import { Eye, PencilIcon, Trash2, type LucideIcon } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

interface PrimaryProps {
    onClick: () => void
    label: string
    icon?: LucideIcon // make optional
}

export const EditAction = ({ onClick }: { onClick: () => void }) => {
    return (
        <Badge
            onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset cursor-pointer">
            <PencilIcon size={ICON_SIZE_2} strokeWidth={2} />
            Edit
        </Badge>)
}


export const ViewAction = ({ onClick }: { onClick: () => void }) => {
    return (
        <Badge
            onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset cursor-pointer">
            <Eye size={ICON_SIZE_2} strokeWidth={2} />
            Edit
        </Badge>)
}

export const DeleteAction = ({ onClick }: { onClick: () => void }) => {
    return (
        <Badge
            onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-sm font-medium text-red-700 ring-1 ring-red-600/10 ring-inset cursor-pointer">
            <Trash2 size={ICON_SIZE_2} strokeWidth={2} />
            Delete
        </Badge>)
}


export const Success = ({ onClick, label, icon: Icon }: { onClick: () => void; label: string; icon: LucideIcon }) => {
    return (
        <Badge onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-green-600/20 ring-inset cursor-pointer">
            <Icon size={ICON_SIZE_2} strokeWidth={2} />
            {label}
        </Badge>)
}

export const Warning = ({ onClick, label, icon: Icon }: { onClick: () => void; label: string; icon: LucideIcon }) => {
    return (
        <Badge onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-sm font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset cursor-pointer">
            <Icon size={ICON_SIZE_2} strokeWidth={2} />
            {label}
        </Badge>)
}

export const Primary = ({ onClick, label, icon: Icon }: PrimaryProps) => {
    return (
        <Badge
            onClick={onClick}
            className="ms-2 inline-flex items-center rounded-md bg-primary px-2 py-1 text-sm font-medium text-white ring-1 ring-yellow-600/20 ring-inset cursor-pointer"
        >
            {Icon && <Icon size={ICON_SIZE_2} strokeWidth={2} className="me-1" />}
            {label}
        </Badge>
    )
}

export const ToggleStatusAction = ({ onClick, is_active }: { onClick: () => void, is_active: boolean }) => {
    return (
        <Switch
            className='ml-2 align-text-bottom'
            checked={is_active}
            onCheckedChange={() => onClick()}
        />
    )
}

const statusLabelVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
                destructive: 'ms-2 inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-sm font-medium text-red-700 ring-1 ring-red-600/10 ring-inset',
                success: 'ms-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-green-600/20 ring-inset',
                warning: 'ms-2 inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-sm font-medium text-yellow-700 ring-1 ring-yellow-600/20 ring-inset'
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

export const StatusLabel = ({ onClick, label, Icon, variant, size }: { onClick?: () => void; label: string; Icon?: LucideIcon, variant?: VariantProps<typeof statusLabelVariants>['variant'], size?: VariantProps<typeof statusLabelVariants>['size'] }) => {
    return (
        <Badge
            onClick={onClick}
            className={cn(statusLabelVariants({ variant, size }))}>
            {Icon && <Icon size={ICON_SIZE_2} strokeWidth={2} />}
            {label}
        </Badge>)
}
